import { createContext, useCallback, useContext, useState } from 'react';
import { getNotifications, markNotificationAsRead } from '@/lib/api/notifications';
import { GetNotifications200ResponseInner } from '@/api-client/autogenerated';
import { toast } from './use-toast';

interface NotificationContextType {
  notifications: GetNotifications200ResponseInner[];
  fetchNotifications: () => Promise<void>;
  clearNotifications: () => void;
  markAsRead: (notificationIds: number[]) => Promise<void>;
}

export const NotificationContext = createContext<NotificationContextType>({
  notifications: [],
  fetchNotifications: async () => {},
  clearNotifications: () => {},
  markAsRead: async () => {},
});

export function useNotificationProvider() {
  const [notifications, setNotifications] = useState<GetNotifications200ResponseInner[]>([]);

  const fetchNotifications = useCallback(async () => {
    try {
      const result = await getNotifications();
      setNotifications(result ?? []);
    } catch {
      setNotifications([]);
    }
  }, []);

  const clearNotifications = useCallback(() => setNotifications([]), []);

  const markAsRead = useCallback(async (notificationIds: number[]) => {
    try {
      await markNotificationAsRead(notificationIds);
      setNotifications((prev) =>
        prev.map((n) =>
          notificationIds.includes(n.Id) ? { ...n, IsRead: true } : n,
        ),
      );
    } catch {
      toast({
        title: 'Failed to mark notification as read',
        description: 'Please try again later',
        variant: 'destructive',
      });
    }
  }, []);

  return { notifications, fetchNotifications, clearNotifications, markAsRead };
}

export function useNotifications() {
  return useContext(NotificationContext);
}
